<script lang="ts">
import FeedbackWidget from '@/components/FeedbackWidget/FeedbackWidget.vue'
import MainMenu from '@/components/header/MainMenu.vue'
import { useMainNavigation } from '@/composables/useMainNavigation'
import DeprecationNotice from '@/views/DeprecationNotice.vue'
import Navigation from '@/views/Navigation.vue'
import Bowser from 'bowser'
import { defineComponent, inject, ref } from 'vue'
import { Auth0PluginApi } from './auth'
import Global from './Global'

export default defineComponent({
  name: 'App',
  components: {
    DeprecationNotice,
    FeedbackWidget,
    Navigation,
    MainMenu
  },
  setup: () => {
    const auth: Auth0PluginApi = inject('auth')

    // auth.isAuthenticated is an async function, not a reactive value, so a template can
    // never read it directly: the function object itself is always truthy, which made the
    // authenticated shell render for signed-out visitors on any route the navigation
    // guards wave through, such as /authentication-error. We resolve the answer here the
    // same way authenticationGuard does, and expose it as a ref the template can react to.
    //
    // The ref is deliberately three-state. It stays undefined until Auth0 has finished
    // restoring the session, so the template can hold back both branches while the answer
    // is unknown. A plain boolean starting at false would show the signed-out branch to a
    // user who is in fact signed in, for as long as Auth0 takes to answer.
    const isAuthenticated = ref<boolean | undefined>(undefined)

    auth.isLoaded
      .then(async () => {
        isAuthenticated.value = await auth.isAuthenticated()
      })
      .catch(() => {
        // Fail closed. If the session cannot be read at all we treat the visitor as signed
        // out, which shows the router view without the authenticated shell, rather than
        // leaving both branches hidden and the page blank forever.
        isAuthenticated.value = false
      })

    const {
      isItAdmin,
      hasMultipleProductsEnabled,
      currentProduct,
      productOptions,
      selectedProduct,
      routedMenuItems,
      onProductSelected
    } = useMainNavigation()

    return {
      isAuthenticated,
      currentProduct,
      routedMenuItems,
      isItAdmin,
      hasMultipleProductsEnabled,
      productOptions,
      selectedProduct,
      onProductSelected
    }
  },
  computed: {
    useUnauthenticatedStyles() {
      return this.$route.path.includes('login')
    },
    isInternetExplorer() {
      const browser = Bowser.getParser(window.navigator.userAgent)
      return browser.getBrowserName() === 'Internet Explorer'
    },
    isSummaryPage() {
      return Global.SUMMARY_PAGES.includes(this.$route.name)
    }
  }
})
</script>

<template>
  <section
    id="app"
    :class="[useUnauthenticatedStyles ? $style.unauthenticated : $style.authenticated]"
  >
    <!-- IE Deprecation Notice -->
    <DeprecationNotice v-if="isInternetExplorer" />

    <!--
      main app div. The checks against isAuthenticated are explicit comparisons because the
      ref has three states: true and false are the two answers, and undefined means Auth0 is
      still restoring the session. While it is undefined neither branch renders, so a signed
      in user is never shown the signed-out branch during that window.
    -->
    <div
      v-else-if="isAuthenticated === true"
      :class="[$style.appContainer, isSummaryPage ? $style.summaryPage : '']"
    >
      <Navigation />
      <div :class="[$style.routerViewWrapper, 'routerViewWrapper']">
        <router-view :key="$route.path" />
      </div>
      <MainMenu
        position="bottom"
        :current-product="currentProduct"
        :routed-menu-items="routedMenuItems"
        :is-it-admin="isItAdmin"
        :has-multiple-products-enabled="hasMultipleProductsEnabled"
        :product-options="productOptions"
        :selected-product="selectedProduct"
        @update:selected-product="onProductSelected"
      />
      <!--
        Inside the authenticated branch only, and mounted once: the widget carries the
        current route into the message, so a second instance would mean a second dialog
        competing for the same modal id. It is absent from the signed-out branch on purpose,
        because a signed-out visitor has no site and no page worth reporting.
      -->
      <FeedbackWidget />
    </div>
    <div v-else-if="isAuthenticated === false">
      <div :class="[$style.routerViewWrapper, 'routerViewWrapper']">
        <router-view :key="$route.path" />
      </div>
    </div>
  </section>
</template>

<style scoped>
#app {
  overflow: hidden;
}
</style>
<style module>
.appContainer {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.appContainer.summaryPage {
  max-width: none;
}

.routerViewWrapper {
  padding: 10px;
  min-height: 1px;
  flex-grow: 1;
  flex-shrink: 1;
  overflow-y: auto;
}

.routerViewWrapper > * {
  margin: 0 auto;
}

.unauthenticated {
  background: #0d4760;
  display: flex;
  min-height: 100%;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.unauthenticated.showBannerMessage {
  padding: 100px 10px 10px 10px;
}

.authenticated {
  background: white;
  position: relative;
  height: 100vh;
}

.unauthenticated p {
  color: white;
  font-size: 16px;
  margin: 20px 0 10px;
}

@media all and (max-width: 750px) {
  .unauthenticated.showBannerMessage {
    padding-top: 200px;
  }
}

@media all and (max-width: 425px) {
  .unauthenticated.showBannerMessage {
    padding-top: 300px;
  }
}

@media all and (max-width: 812px) {
  .appContainer {
    /* height: 90vh; */
    height: 90vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}
</style>
